import { useState } from 'react'
import { Calendar } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { ConfirmationDialog } from '@/components/common/ConfirmationDialog'
import { usePlanCalendar, useDetachPlanCalendar } from '@/hooks/usePlanCalendar'
import { CalendarEventPickerDialog } from './CalendarEventPickerDialog'

interface CalendarAssignmentCardProps {
  planId: string
  isSeasonal?: boolean
}

export function CalendarAssignmentCard({
  planId,
  isSeasonal = false,
}: CalendarAssignmentCardProps) {
  const [isHovered, setIsHovered] = useState(false)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [confirmRemoveOpen, setConfirmRemoveOpen] = useState(false)

  const { data: planCalendar, isLoading: isLoadingCalendar } = usePlanCalendar(
    planId,
    isSeasonal
  )
  const detachMutation = useDetachPlanCalendar(planId, isSeasonal)

  const handleRemove = () => {
    detachMutation.mutate(undefined, {
      onSuccess: () => {
        setConfirmRemoveOpen(false)
      },
    })
  }

  const hasCalendar = !!planCalendar?.calendar_id && !!planCalendar?.event_id

  return (
    <>
      <div
        className="rounded-[14px] border border-[#f3f4f6] bg-[#f9fafb] p-4"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex size-[36px] shrink-0 items-center justify-center rounded-[10px] bg-white shadow-[0px_1px_3px_0px_rgba(0,0,0,0.1),0px_1px_2px_0px_rgba(0,0,0,0.1)]">
              <Calendar className="h-5 w-5 text-[#6a7282]" />
            </div>
            <div>
              <p className="text-[14px] text-[#6a7282]">
                {isSeasonal
                  ? 'Seasonal Calendar'
                  : 'Supply Chain Event Calendar'}
              </p>
              {isLoadingCalendar ? (
                <div className="h-5 w-24 animate-pulse rounded bg-gray-200" />
              ) : hasCalendar ? (
                <>
                  <p className="text-[16px] font-semibold text-[#101828]">
                    {planCalendar.calendar_name}
                  </p>
                  <p className="text-[13px] text-[#6a7282]">
                    {planCalendar.event_name}
                  </p>
                </>
              ) : (
                <p className="text-[16px] font-semibold text-[#101828]">
                  None assigned
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            {hasCalendar && isHovered && (
              <button
                type="button"
                className="cursor-pointer text-sm text-destructive underline hover:text-destructive/80"
                onClick={() => setConfirmRemoveOpen(true)}
              >
                Remove
              </button>
            )}

            {(isHovered || dialogOpen) && (
              <Button
                variant="ghost"
                size="sm"
                className="h-8 gap-1.5 cursor-pointer text-[#CF1F2E] hover:bg-[#fef2f2] hover:text-[#CF1F2E]"
                onClick={() => setDialogOpen(true)}
              >
                {hasCalendar ? 'Change' : 'Assign'}
              </Button>
            )}
          </div>
        </div>
      </div>

      <CalendarEventPickerDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        planId={planId}
        isSeasonal={isSeasonal}
        currentCalendarId={planCalendar?.calendar_id}
        currentEventId={planCalendar?.event_id}
      />

      <ConfirmationDialog
        open={confirmRemoveOpen}
        onOpenChange={setConfirmRemoveOpen}
        title={isSeasonal ? 'Remove Seasonal Calendar' : 'Remove Calendar'}
        description={`Are you sure you want to remove the ${isSeasonal ? 'seasonal ' : ''}calendar from this plan? This will not delete the calendar itself.`}
        confirmText="Remove"
        cancelText="Cancel"
        variant="destructive"
        isLoading={detachMutation.isPending}
        onConfirm={handleRemove}
      />
    </>
  )
}
